import { getToken, onMessage, deleteToken } from 'firebase/messaging';
import { doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db, getMessagingInstance } from './firebase';

const PUSH_TOKEN_KEY = 'votebeats_push_token';
const PUSH_ASKED_KEY = 'votebeats_push_asked';

/**
 * Request push notification permission, get FCM token, and save to Firestore.
 * @param identityId - DJ's Firebase uid OR attendee's anonymous oderId
 * @param eventId - set for attendees (scoped to event), null for DJs (global)
 * @returns the FCM token string, or null if denied/unsupported
 */
export async function requestPushPermission(
  identityId: string,
  eventId?: string
): Promise<string | null> {
  try {
    const messaging = await getMessagingInstance();
    if (!messaging) return null;

    if (typeof Notification === 'undefined') return null;
    const permission = await Notification.requestPermission();
    localStorage.setItem(PUSH_ASKED_KEY, 'true');

    if (permission !== 'granted') return null;

    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: await navigator.serviceWorker.register(
        '/firebase-messaging-sw.js'
      ),
    });

    if (!token) return null;

    // Save token to Firestore — doc ID = token string for natural dedup
    await setDoc(doc(db, 'pushTokens', token), {
      token,
      identityId,
      eventId: eventId || null,
      platform: 'web',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    localStorage.setItem(PUSH_TOKEN_KEY, token);
    return token;
  } catch (err) {
    console.error('Failed to get push token:', err);
    return null;
  }
}

/**
 * Remove the current push token from Firestore and localStorage.
 */
export async function removePushToken(): Promise<void> {
  try {
    const token = localStorage.getItem(PUSH_TOKEN_KEY);
    if (token) {
      await deleteDoc(doc(db, 'pushTokens', token));
    }

    const messaging = await getMessagingInstance();
    if (messaging) {
      await deleteToken(messaging);
    }

    localStorage.removeItem(PUSH_TOKEN_KEY);
  } catch (err) {
    console.error('Failed to remove push token:', err);
  }
}

/**
 * Check if push notifications are currently enabled (token stored locally).
 */
export function isPushEnabled(): boolean {
  return (
    typeof Notification !== 'undefined' &&
    Notification.permission === 'granted' &&
    !!localStorage.getItem(PUSH_TOKEN_KEY)
  );
}

export type PushPermissionState = 'unsupported' | 'default' | 'granted' | 'denied';

export function pushPermissionState(): PushPermissionState {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

/**
 * When the browser already allows notifications but this device has no stored token,
 * register one without prompting (requestPermission resolves at once once granted).
 */
export async function ensurePushTokenIfGranted(identityId: string, eventId: string): Promise<string | null> {
  if (pushPermissionState() !== 'granted' || isPushEnabled()) return null;
  return requestPushPermission(identityId, eventId);
}

/**
 * Check if the user has already been asked about push notifications.
 */
export function hasBeenAskedAboutPush(): boolean {
  return localStorage.getItem(PUSH_ASKED_KEY) === 'true';
}

/**
 * Set up a foreground message listener that calls the provided callback.
 * Returns an unsubscribe function.
 */
export async function setupForegroundListener(
  onNotification: (title: string, body: string, url?: string, type?: string) => void
): Promise<(() => void) | null> {
  const messaging = await getMessagingInstance();
  if (!messaging) return null;

  const unsubscribe = onMessage(messaging, (payload) => {
    const title = payload.notification?.title || 'VoteBeats';
    const body = payload.notification?.body || '';
    const url = payload.data?.url;
    onNotification(title, body, url, payload.data?.type);
  });

  return unsubscribe;
}
